import React, { useEffect, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import { API_DOMAIN } from '../../constants';
import { SiteEnvironmentType } from '@edgebox/sync-core-data-definitions';

const isLocal = [SiteEnvironmentType.Local, SiteEnvironmentType.Testing, SiteEnvironmentType.Staging].includes(
  process.env.REACT_APP_ENVIRONMENT_TYPE as any
);

export function isTestPasswordLoginEnabled(): boolean {
  return isLocal;
}

export function TestPasswordLogin(): React.ReactElement {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const apiBaseUrl = useMemo(() => `//${API_DOMAIN}`, []);

  useEffect(() => {
    if (!isLocal) {
      return;
    }

    const search = new URLSearchParams(window.location.search);
    const jwt = search.get('jwt');
    if (!jwt) {
      return;
    }

    // Client-side cookie storage only.
    document.cookie = `content_sync_test_jwt=${jwt}; Path=/; SameSite=Lax`;
    window.location.replace('/');
  }, []);

  if (!isLocal) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">This login is only available in local environments.</Alert>
      </Container>
    );
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();

    setIsLoading(true);
    setError(undefined);

    try {
      const response = await fetch(`${apiBaseUrl}/authentication/test/token`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || `Request failed with ${response.status}`);
      }

      const json = (await response.json()) as { access_token?: string };
      if (!json?.access_token) {
        throw new Error('No access_token returned.');
      }

      // Client-side cookie storage only.
      document.cookie = `content_sync_test_jwt=${json.access_token}; Path=/; SameSite=Lax`;
      window.location.href = '/';
    } catch (e: any) {
      setError(e?.message || 'Login failed.');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Container className="mt-5" style={{ maxWidth: 560 }}>
      <Card>
        <Card.Body>
          <Card.Title>Local test login</Card.Title>
          <Card.Subtitle className="mb-3 text-muted">Email/password (Auth0 test connection)</Card.Subtitle>

          {error && <Alert variant="danger">{error}</Alert>}

          <Form onSubmit={onSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Username</Form.Label>
              <Form.Control type="text" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" required />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </Form.Group>

            <Button type="submit" variant="primary" disabled={isLoading}>
              {isLoading ? 'Logging in…' : 'Login'}
            </Button>
          </Form>
        </Card.Body>
      </Card>

      <Alert variant="warning" className="mt-3">
        This is a local-test-only feature. It stores the JWT in a browser cookie named <strong>content_sync_test_jwt</strong>.
      </Alert>
    </Container>
  );
}
